import type { ClientVerdict, Hit } from "@ranking-arena/shared/schemas";
import type { Mode } from "@ranking-arena/shared/types";

import { canonicalize } from "./canonical";
import { getFirebaseAuth } from "./firebase";

/**
 * Typed fetch wrapper for the arena backend. Injects the current user's
 * Firebase ID token on every request; throws `ApiError` with differentiated
 * codes on non-2xx so UI can select copy.
 */

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
    readonly body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function idToken(): Promise<string | null> {
  const user = getFirebaseAuth().currentUser;
  if (!user) return null;
  return user.getIdToken();
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await idToken();
  const headers = new Headers(init.headers);
  if (token) headers.set("authorization", `Bearer ${token}`);
  if (init.body && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }
  const res = await fetch(path, { ...init, headers });
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      // ignore
    }
    const code =
      typeof body === "object" && body && "error" in body
        ? String((body as { error: unknown }).error)
        : undefined;
    const vespaCode =
      typeof body === "object" && body && "code" in body
        ? String((body as { code: unknown }).code)
        : undefined;
    throw new ApiError(
      `API ${res.status} on ${path}${vespaCode ? `: ${vespaCode}` : code ? `: ${code}` : ""}`,
      res.status,
      vespaCode ?? code,
      body,
    );
  }
  return (await res.json()) as T;
}

// ---- Shared wire types ----

// Re-export so pages/components can import from one place.
export type { ClientVerdict, Hit, Mode };

// ---- Endpoints (unchanged pre-pivot) ----

export interface ModesResponse {
  modes_enabled: Record<Mode, boolean>;
  default_mode: Mode | null;
}
export const getModes = () => request<ModesResponse>("/api/modes");

export interface ProfilesResponse {
  profiles: Array<{
    api_name: string;
    type: Mode;
    display_label: string;
    env: "shared" | "arena";
    schema_block: string;
    inheritance_chain: string[];
  }>;
}
export const getProfiles = (mode?: string) =>
  request<ProfilesResponse>(`/api/profiles${mode ? `?mode=${encodeURIComponent(mode)}` : ""}`);

// ---- 1v1 arena endpoints ----

export interface SearchRequest {
  mode: Mode;
  profileA: string;
  profileB: string;
  query: string;
}

export interface SearchResponse {
  queryId: string;
  hitsLeft: Hit[];
  hitsRight: Hit[];
  latencyLeftMs: number;
  latencyRightMs: number;
}

export function postSearch(req: SearchRequest): Promise<SearchResponse> {
  return request<SearchResponse>("/api/search", {
    method: "POST",
    body: JSON.stringify({
      mode: req.mode,
      profileA: req.profileA,
      profileB: req.profileB,
      query: req.query,
    }),
  });
}

export interface VoteRequest {
  queryId: string;
  verdict: ClientVerdict;
}

export function postVote(req: VoteRequest): Promise<{ ok: true }> {
  // snake_case on the wire to match the backend schema
  return request<{ ok: true }>("/api/votes", {
    method: "POST",
    body: JSON.stringify({
      query_id: req.queryId,
      verdict: req.verdict,
    }),
  });
}

// ---- Results ----

export interface PairLatency {
  count: number;
  mean_ms: number;
  p50_ms: number;
  p95_ms: number;
}

export interface PerQueryRow {
  queryText: string;
  profile1_better: number;
  profile2_better: number;
  both_good: number;
  both_bad: number;
}

export interface PerVoterRow {
  voterEmail: string;
  profile1_better: number;
  profile2_better: number;
  both_good: number;
  both_bad: number;
  total: number;
}

export interface PairResults {
  mode: Mode;
  profile1: string;
  profile2: string;
  counts: {
    profile1_better: number;
    profile2_better: number;
    both_good: number;
    both_bad: number;
  };
  indexVersions: Record<string, number>;
  latencyProfile1: PairLatency;
  latencyProfile2: PairLatency;
  lastVoteAt: string | null;
  lastIndexVersion: string | null;
  perQuery: PerQueryRow[];
  perVoter: PerVoterRow[];
}

export function getResults(req: {
  mode: Mode;
  profileA: string;
  profileB: string;
}): Promise<PairResults> {
  // Canonicalize client-side so the querystring matches the pair_aggregates doc ID.
  const { profile1, profile2 } = canonicalize(req.profileA, req.profileB);
  const qs = new URLSearchParams({
    mode: req.mode,
    profile1,
    profile2,
  });
  return request<PairResults>(`/api/results?${qs.toString()}`);
}
